<template>
  <div class="mb-4">
    <h1 class="h1">{{ trans('auth.register.title') }}</h1>
    <p class="py-1">{{ trans('auth.register.intro') }}</p>
  </div>

  <div v-if="form.errors.invitation" class="alert alert-danger">
    {{ form.errors.invitation }}
  </div>

  <form role="form" @submit.prevent="submit">
    <div class="flex justify-content-between gap">
      <form-input
        id="first-name-field"
        type="text"
        name="first_name"
        containerClass="w-full form-group"
        v-model="form.first_name"
        :error="form.errors.first_name"
        :label-text="trans('common.first_name')"
        :placeholder="trans('common.first_name')"
        autofocus
        required
      />

      <form-input
        id="last-name-field"
        type="text"
        name="last_name"
        containerClass="w-full form-group"
        v-model="form.last_name"
        :error="form.errors.last_name"
        :label-text="trans('common.last_name')"
        :placeholder="trans('common.last_name')"
        required
      />
    </div>

    <div class="flex justify-content-between gap">
      <form-input
        id="company-name-field"
        type="text"
        name="company_name"
        containerClass="w-full form-group"
        v-model="form.company_name"
        :error="form.errors.company_name"
        :label-text="trans('common.company_name')"
        :placeholder="trans('common.company_name')"
        :required="asPartner"
      />

      <form-input
        id="phone-field"
        type="tel"
        name="phone"
        containerClass="w-full form-group"
        v-model="form.phone"
        :error="form.errors.phone"
        :label-text="trans('common.phone_number')"
        :placeholder="trans('common.phone_number')"
      />
    </div>

    <form-input
      id="email-field"
      type="email"
      name="email"
      containerClass="form-group"
      v-model="form.email"
      :error="form.errors.email"
      :label-text="trans('common.email')"
      :placeholder="trans('common.email')"
      required
    />

    <div class="flex justify-content-between gap">
      <form-input
        id="password-field"
        type="password"
        name="password"
        containerClass="form-group w-full"
        v-model="form.password"
        :error="form.errors.password"
        :label-text="trans('common.password')"
        :placeholder="trans('common.password')"
        required
      />

      <form-input
        id="password-confirm-field"
        type="password"
        name="password_confirmation"
        containerClass="form-group w-full"
        v-model="form.password_confirmation"
        :error="form.errors.password_confirmation"
        :label-text="trans('common.confirm_password')"
        :placeholder="trans('common.confirm_password')"
        required
      />
    </div>

    <div class="form-group">
      <form-input
        id="register-applications"
        type="multi-select"
        v-model="form.applications"
        track-by="id"
        label="name"
        :options="applications"
        :error="form.errors.applications"
        :label-text="trans('auth.register.which_applications')"
        :placeholder="trans('common.applications')"
        required
      />
    </div>

    <div class="flex justify-content-between gap">
      <form-input
        id="user-billing_address"
        container-class="form-group w-full"
        name="billing_address"
        type="text"
        v-model="form.billing_address"
        :label-text="trans('common.address')"
        :placeholder="trans('common.address')"
        :error="form.errors.billing_address"
        required
      />

      <form-input
        id="user-billing_address-2"
        container-class="form-group w-full"
        name="billing_address_2"
        type="text"
        v-model="form.billing_address_line_2"
        :label-text="trans('common.address_line_2')"
        :placeholder="trans('common.address_line_2')"
        :error="form.errors.billing_address_line_2"
      />
    </div>

    <div class="flex justify-content-between gap">
      <form-input
        id="user-billing_zip"
        container-class="form-group w-full"
        name="billing_zip"
        type="text"
        v-model="form.billing_zip"
        :label-text="trans('common.postal_code')"
        :placeholder="trans('common.postal_code')"
        :error="form.errors.billing_zip"
        required
      />

      <form-input
        id="user-billing_city"
        container-class="form-group w-full"
        name="billing_city"
        type="text"
        v-model="form.billing_city"
        :label-text="trans('common.city')"
        :placeholder="trans('common.city')"
        :error="form.errors.billing_city"
        required
      />
    </div>

    <div class="flex justify-content-between gap">
      <form-input
        id="user-billing_state"
        container-class="form-group w-full"
        name="billing_state"
        type="text"
        v-model="form.billing_state"
        :label-text="trans('common.state')"
        :placeholder="trans('common.state')"
        :error="form.errors.billing_state"
      />

      <form-input
        id="user-billing_country"
        class="mt-2"
        container-class="form-group w-full"
        name="billing_country"
        type="select"
        v-model="form.billing_country"
        :options="countries"
        :allow-empty="false"
        track-by="code"
        label="name"
        :label-text="trans('common.country')"
        :error="form.errors.billing_country"
        required
      />
    </div>

    <div>
      <div class="form_row form-group mb-0">
        <form-input
          id="terms"
          type="checkbox"
          v-model="form.terms"
          :error="form.errors.terms"
          :label-text="trans('auth.accept_terms_of_service')"
        />
      </div>
      <div class="form_row form-group mt-0">
        <form-input
          id="processing_agreement"
          type="checkbox"
          v-model="form.processing_agreement"
          :error="form.errors.processing_agreement"
          :label-text="trans('auth.accept_processing_agreement')"
        />

        <form-button :processing="form.processing" :value="trans('auth.register.title')"/>
      </div>
    </div>
    <div class="flex justify-content-between align-items-center mt-4 pt-4 border-top">
      <div>
        {{ trans('auth.register.existing_account') }}
        <inertia-link class="btn btn-link font_bold" :href="localeRoute('login')" v-text="trans('auth.login.title')"/>
      </div>

      <language-toggle :route-to="{ name: 'register' }"/>
    </div>
  </form>
</template>
<script>
import AuthLayout from '@/Layouts/AuthLayout';

export default {
  layout: AuthLayout,
};
</script>

<script setup>
import {onBeforeMount, ref} from 'vue';
import {router, useForm} from '@inertiajs/vue3';
import LanguageToggle from '@/components/Shared/LanguageToggle';
import FormInput from '@/components/Shared/Form/Input/Index';
import FormButton from '@/components/Shared/Form/Button';
import axios from "axios";
import {useLocales} from "@/composables/locales";

const props = defineProps({
  asPartner: {
    type: Boolean,
    default: false
  }
})

const currentLocale = useLocales().currentLocale
const localeRoute = useLocales().localeRoute

const applications = ref([]);
const countries = ref([]);

const form = useForm({
  as_partner: props.asPartner,
  profile_language: currentLocale,
  company_name: null,
  first_name: null,
  last_name: null,
  email: null,
  phone: null,
  password: null,
  password_confirmation: null,
  applications: [],
  billing_address: null,
  billing_address_line_2: null,
  billing_city: null,
  billing_state: null,
  billing_zip: null,
  billing_country: null,
  terms: false,
  processing_agreement: false,
  invitation: route().params.invitation,
});

onBeforeMount(async () => {
  await Promise.all([
    fetchCountries(),
    fetchApplications(),
  ])
});

const fetchCountries = () => axios
  .get(route('countries'))
  .then(({data}) => {
    countries.value = data.data.countries;
  })

const fetchApplications = () => axios
  .get('/register/all-applications')
  .then(({data}) => {
    applications.value = data.data.applications;
  })

const submit = () => {
  form
    .transform(data => ({
      ...data,
      billing_country: data.billing_country?.code,
    }))
    .post(localeRoute('register', route().params), {
      onSuccess: result => {
        gtag_report_conversion(window.location.href, result.props.user.email);
      },
    });
};

window.onload = () => {
  document.body.id = 'register';

  window.dataLayer = window.dataLayer || [];

  function gtag() {
    dataLayer.push(arguments);
  }

  gtag('js', new Date());
  gtag('config', 'AW-432758964');

  window.gtag_report_conversion = function (url, customerEmail) {
    var callback = function () {
      if (typeof url != 'undefined') {
        window.location = url;
      }
    };
    gtag('event', 'conversion', {
      send_to: 'AW-432758964/KjY4CIzQ3aQDELTBrc4B',
      event_callback: callback,
      customer_email: customerEmail,
    });
    return false;
  };
};
</script>
